import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatINR, formatDateTime } from '@/lib/format';
import { Wallet, RefreshCw, Zap } from 'lucide-react';

/**
 * MakePaymentDialog — Phase 10
 *
 * SA-only payment entry with optional per-purchase allocations.
 *
 * Behavior:
 *  - Loads outstanding purchases for the vendor (oldest first — FIFO).
 *  - User enters total Amount + Reference + Note.
 *  - User can allocate the amount across one or many purchase rows.
 *  - "Auto-allocate (FIFO)" button distributes from oldest purchase first.
 *  - Sum of allocations must be <= Amount; remainder becomes
 *    "Unallocated Credit" on the payment entry.
 */
export function MakePaymentDialog({ open, onOpenChange, vendor, onCreated }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [allocs, setAllocs] = useState({}); // {purchase_entry_id: amountStr}

  const reset = () => {
    setAmount(''); setReference(''); setNote(''); setAllocs({});
  };

  const loadOutstanding = async () => {
    if (!vendor?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/masters/vendors/${vendor.id}/outstanding-purchases`);
      setPurchases(data.purchases || []);
    } catch (e) { toast.error(extractError(e, 'Failed to load purchases')); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && vendor?.id) { reset(); loadOutstanding(); }
    // eslint-disable-next-line
  }, [open, vendor?.id]);

  // Sum of currently allocated amounts
  const allocSum = useMemo(() => {
    return Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0);
  }, [allocs]);

  const remaining = useMemo(() => {
    const amt = Number(amount) || 0;
    return Math.max(0, amt - allocSum);
  }, [amount, allocSum]);

  const overAllocated = allocSum - (Number(amount) || 0) > 0.001;

  const autoAllocate = () => {
    const amt = Number(amount) || 0;
    if (amt <= 0) { toast.error('Enter payment amount first'); return; }
    let left = amt;
    const next = {};
    // purchases are already oldest-first from backend
    for (const p of purchases) {
      if (left <= 0) break;
      const out = Number(p.outstanding_amount) || 0;
      if (out <= 0) continue;
      const take = Math.min(out, left);
      next[p.id] = take.toFixed(2);
      left = Math.max(0, left - take);
    }
    setAllocs(next);
  };

  const clearAllocs = () => setAllocs({});

  const setAllocAmount = (id, val, maxOut) => {
    const v = val.replace(/^\s+|\s+$/g, '');
    if (v === '') { setAllocs((a) => { const next = { ...a }; delete next[id]; return next; }); return; }
    const num = Number(v);
    if (Number.isNaN(num) || num < 0) return;
    const capped = Math.min(num, maxOut);
    setAllocs((a) => ({ ...a, [id]: String(capped) }));
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('Enter payment amount'); return; }
    if (overAllocated) { toast.error(`Allocated (₹${allocSum.toFixed(2)}) exceeds amount (₹${amt.toFixed(2)})`); return; }
    const allocations = Object.entries(allocs)
      .map(([id, a]) => ({ purchase_entry_id: id, amount: Number(a) }))
      .filter((x) => x.amount > 0);
    setSaving(true);
    try {
      const { data } = await api.post(`/masters/vendors/${vendor.id}/payment`, {
        amount: amt,
        reference,
        note,
        allocations,
      });
      const unalloc = Number(data?.unallocated_amount || 0);
      toast.success(
        unalloc > 0
          ? `Payment of ₹${amt.toLocaleString('en-IN')} recorded · ₹${unalloc.toLocaleString('en-IN')} unallocated`
          : `Payment of ₹${amt.toLocaleString('en-IN')} recorded and allocated`
      );
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(extractError(e, 'Payment failed')); }
    finally { setSaving(false); }
  };

  const totalOutstanding = useMemo(() => purchases.reduce((s, p) => s + (Number(p.outstanding_amount) || 0), 0), [purchases]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="vendor-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet size={16} /> Make Payment to {vendor?.name || 'Vendor'}</DialogTitle>
          <DialogDescription>
            Record a payment and optionally allocate it across outstanding purchase entries.
            Allocations are oldest-first (FIFO) by default. Unallocated remainder becomes vendor credit.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="p-3 col-span-1 bg-emerald-50 border-emerald-200">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700">Total Outstanding</div>
            <div className="font-mono text-lg text-emerald-800" data-testid="vendor-payment-total-outstanding">{formatINR(totalOutstanding)}</div>
          </Card>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <Label>Payment Amount (₹) <span className="text-rose-600">*</span></Label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="vendor-payment-amount-input" />
            </div>
            <div>
              <Label>Reference (cheque / UPI / bank ref)</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. NEFT 123456" data-testid="vendor-payment-reference-input" />
            </div>
            <div className="col-span-2">
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="vendor-payment-note-input" />
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-medium text-[var(--ink-navy)]">Allocate Against Purchases (oldest first)</div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`text-[11px] font-mono ${overAllocated ? 'bg-rose-100 text-rose-700' : remaining > 0 ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`} data-testid="vendor-payment-alloc-summary">
                Allocated {formatINR(allocSum)} of {formatINR(Number(amount) || 0)} · {remaining > 0 ? `Unallocated ${formatINR(remaining)}` : 'Fully allocated'}
              </Badge>
              <Button type="button" variant="outline" size="sm" onClick={autoAllocate} data-testid="vendor-payment-auto-allocate-button"><Zap size={12} className="mr-1" /> Auto-allocate (FIFO)</Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearAllocs} data-testid="vendor-payment-clear-allocs-button"><RefreshCw size={12} className="mr-1" /> Clear</Button>
            </div>
          </div>
          <Card className="overflow-hidden">
            <div className="max-h-[40vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">Purchase Date</th>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">Material</th>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">Reference</th>
                    <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">Amount</th>
                    <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">Already Paid</th>
                    <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">Outstanding</th>
                    <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">Allocate (₹)</th>
                  </tr>
                </thead>
                <tbody data-testid="vendor-payment-purchases-tbody">
                  {loading && (
                    <tr><td colSpan={7} className="text-center py-6 text-slate-500">Loading…</td></tr>
                  )}
                  {!loading && purchases.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-6 text-slate-500 text-sm">No outstanding purchases. Payment will be recorded as unallocated credit.</td></tr>
                  )}
                  {!loading && purchases.map((p) => {
                    const out = Number(p.outstanding_amount) || 0;
                    const allocVal = allocs[p.id] || '';
                    return (
                      <tr key={p.id} className="border-t border-[var(--divider)]" data-testid={`vendor-payment-purchase-row-${p.id}`}>
                        <td className="px-3 py-2 text-xs text-slate-600">{formatDateTime(p.timestamp)}</td>
                        <td className="px-3 py-2 text-xs">
                          <Badge variant="secondary" className="text-[10px] capitalize">{p.material_kind || (p.packet_no ? 'diamond' : 'other')}</Badge>
                          {p.packet_no && <span className="font-mono text-[11px] text-slate-600 ml-1.5">{p.packet_no}</span>}
                          {p.entry_type === 'convert_to_stock' && <span className="ml-1.5 text-[10px] text-purple-700">(converted)</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[180px]" title={p.reference || ''}>{p.reference || '—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatINR(p.amount)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">{formatINR(p.paid_amount || 0)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700">{formatINR(out)}</td>
                        <td className="px-3 py-2 text-right">
                          <Input
                            type="number" step="0.01" min="0" max={out}
                            value={allocVal}
                            onChange={(e) => setAllocAmount(p.id, e.target.value, out)}
                            className="h-8 w-28 text-right font-mono text-xs"
                            placeholder="0"
                            data-testid={`vendor-payment-alloc-input-${p.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            className="luxury-button"
            disabled={saving || !amount || Number(amount) <= 0 || overAllocated}
            data-testid="vendor-payment-submit-button"
          >
            {saving ? 'Recording…' : `Record Payment ${formatINR(Number(amount) || 0)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MakePaymentDialog;
